import * as React from 'react';
import { Alert, Button, FormPanel, IconButton, Input, Modal, Select, SubmitButton } from '@ixicare/ui';
import { ApiKeyType, CreatedApiKeyProps } from '@ixicare/resources';
import { Copy } from 'lucide-react';
import { t } from '~i18n';
import { getFieldError } from '~utils/error';
import { useStores } from '~store/utils';
import { useSiteApiKeyMutations } from '../../hooks/useSiteApiKeys';

// An `<input type="date">` value is a local calendar day; the key stays valid until that day ends.
const endOfDay = (day: string) => new Date(`${day}T23:59:59`);

const today = () => {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

/**
 * Creates a key and then shows it, once. The backend only stores a hash, so closing this modal is
 * the last chance to copy the full key.
 */
export function CreateApiKeyModal({ siteUuid, onClose }: { siteUuid: string; onClose: () => void }) {
  const { create } = useSiteApiKeyMutations(siteUuid);
  const [name, setName] = React.useState('');
  const [keyType, setKeyType] = React.useState<ApiKeyType>(ApiKeyType.API);
  const [expiryDay, setExpiryDay] = React.useState('');
  const [createdKey, setCreatedKey] = React.useState<CreatedApiKeyProps | null>(null);

  const typeOptions = [
    { value: ApiKeyType.API, label: t('site.developer.keyType.api') },
    { value: ApiKeyType.WEBSOCKET, label: t('site.developer.keyType.websocket') },
  ];

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    create.mutate(
      { name: name.trim(), keyType, expiresAt: expiryDay ? endOfDay(expiryDay) : null },
      { onSuccess: setCreatedKey }
    );
  };

  const error = create.error ?? undefined;
  const nameError = getFieldError('name', error);
  const expiryError = getFieldError('expiresAt', error);
  const otherError = error && !nameError && !expiryError && !getFieldError('keyType', error);

  return (
    <Modal onClose={onClose} style={{ maxWidth: 500 }}>
      <h3 className="text-center mb-30">
        {createdKey ? t('site.developer.createKey.createdTitle') : t('site.developer.keys.create')}
      </h3>
      {createdKey ? (
        <CreatedKey apiKey={createdKey.apiKey} onClose={onClose} />
      ) : (
        <FormPanel>
          <form onSubmit={handleSubmit}>
            <div className="mb-30">
              <Input
                label={t('billing.table.name')}
                value={name}
                onValueChange={setName}
                maxLength={100}
                error={nameError}
                required
                autoFocus
              />
            </div>
            <div className="mb-30">
              <Select
                label={t('billing.table.type')}
                options={typeOptions}
                value={keyType}
                onChange={setKeyType}
                error={getFieldError('keyType', error)}
                required
              />
            </div>
            <Input
              type="date"
              label={t('site.developer.createKey.expiresAt')}
              value={expiryDay}
              min={today()}
              onValueChange={setExpiryDay}
              error={expiryError}
            />
            <p className="text-muted mt-10">{t('site.developer.createKey.expiresAtHint')}</p>
            {otherError && <Alert type="error">{t('site.developer.createKey.error')}</Alert>}
            <div className="d-flex justify-content-end mt-30">
              <SubmitButton type="submit" loading={create.isPending} disabled={!name.trim()}>
                {t('site.developer.keys.create')}
              </SubmitButton>
            </div>
          </form>
        </FormPanel>
      )}
    </Modal>
  );
}

function CreatedKey({ apiKey, onClose }: { apiKey: string; onClose: () => void }) {
  const { notifications } = useStores();

  const copy = () => {
    navigator.clipboard
      .writeText(apiKey)
      .then(() =>
        notifications.addUserActionNotification({
          notificationType: 'success',
          message: t('site.developer.createKey.copied'),
        })
      )
      .catch(() => {
        // Clipboard access can be refused; the key stays selectable in the field.
      });
  };

  return (
    <>
      <Alert type="warning">{t('site.developer.createKey.showOnce')}</Alert>
      <div className="mt-30 mb-30">
        <Input
          label={t('site.developer.keys.key')}
          value={apiKey}
          readOnly
          onFocus={(e) => e.target.select()}
          adornment={
            <span title={t('site.developer.createKey.copy')}>
              <IconButton lucide={Copy} smallIcon onClick={copy} />
            </span>
          }
        />
      </div>
      <div className="d-flex justify-content-end">
        <Button type="button" onClick={onClose}>
          {t('columnManager.done')}
        </Button>
      </div>
    </>
  );
}
